import { useOnboardingStepView } from "analytics/onboarding";
import React, { useContext } from "react";
import { ONBOARDING_PHONE_GUTTER } from "features/onboarding/common/onboardingUi";
import useMedia from "utils/hooks/useMedia";
import { EXTERNAL_INVITE_SUCCESS_PATH, TABLET } from "common/constants";
import { OnboardingDetailedStep } from "@shared/features/onboarding/types";
import { OnboardingContext } from "@shared/features/onboarding/Onboarding.context";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import { AccountFormContext } from "@shared/features/common/AccountForm.context";
import useQuery from "utils/hooks/useQuery";
import ConnectExchange from "features/portfolio/CreateAccount/ConnectExchange";
import { QuickConnectContext } from "@shared/features/onboarding/QuickConnect.context";
import { AddClientResponse } from "@shared/types";
import styled, { useTheme } from "styled-components";
import { reveal } from "features/onboarding/common/onboardingUi";
import Flex from "common/Boxes/Flex.tsx";
import { useBottomNavigator } from "utils/hooks/misc.tsx";
import { ExternalConnectAccountContext } from "@shared/features/configuration/ExternalConnectAccount.context.ts";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery.ts";
import GuidedDemoSetup from "features/onboarding/guided/GuidedDemoSetup";

interface ConnectExchangeProps {
  getErrorNotification: (
    description: string,
    title?: string | undefined
  ) => void;
  getSuccessMessage?: (
    response?: AddClientResponse,
    exchangesList?: string[]
  ) => void;
}

const OnboardingConnectExchange = ({
  getErrorNotification,
  getSuccessMessage,
}: ConnectExchangeProps) => {
  const theme = useTheme();
  const { historyPush } = useHistoryWithQuery();
  const { changeStep } = useContext(OnboardingContext) || {};
  const { isExternalConnectAccount } =
    useContext(ExternalConnectAccountContext) || {};
  const { isDemoWorkspace, exchangesLoading } = useContext(AccountFormContext)!;
  const { quickConnectLoading } = useContext(QuickConnectContext) || {};
  const isTablet = useMedia([`(max-width: ${TABLET})`], [true], false);
  const query = useQuery();
  const primaryExchange = query.get("exchange") || undefined;
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const demoSetup =
    isDemoWorkspace && query.get("guided") === "1" && !isExternalConnectAccount;
  // The live connect form (the demo setup records its own step).
  useOnboardingStepView(
    demoSetup || isExternalConnectAccount ? undefined : "connect_exchange",
    { exchange: primaryExchange }
  );

  if (
    isDemoWorkspace &&
    query.get("guided") === "1" &&
    !isExternalConnectAccount
  ) {
    return <GuidedDemoSetup />;
  }

  const connectExchange = (
    <ConnectExchange
      primaryExchange={primaryExchange}
      isOnboarding={true}
      onSubmitSuccess={() => {
        if (isExternalConnectAccount) {
          historyPush(EXTERNAL_INVITE_SUCCESS_PATH);
        } else {
          changeStep?.(OnboardingDetailedStep.TRADING_METHOD);
        }
      }}
      getErrorNotification={getErrorNotification}
      getSuccessMessage={getSuccessMessage}
      showAccountTypeSwitch={false}
    />
  );

  return (
    <Step>
      <LoadingSpinnerOverlay
        isLoading={quickConnectLoading || !primaryExchange || exchangesLoading}
        hideContentWhileLoading={true}
        backgroundColor={theme.colors.layoutBgColor}
        spinnerSize="large"
        wrap={true}
        wrapStyle={{ width: "100%", minHeight: 200 }}
      >
        {isTablet ? (
          <MobileWrapper>
            <ContentWrapper
              $isBottomNavigator={
                isBottomNavigator && !isExternalConnectAccount
              }
            >
              {connectExchange}
            </ContentWrapper>
          </MobileWrapper>
        ) : (
          connectExchange
        )}
      </LoadingSpinnerOverlay>
    </Step>
  );
};

export default OnboardingConnectExchange;

const Step = styled.div`
  width: 100%;
  max-width: 1000px;
  margin: 0 auto;
  ${reveal(1)};
`;
const MobileWrapper = styled(Flex)`
  flex-direction: column;
  flex: 1;
  overflow: hidden;
  height: 100%;
`;
const ContentWrapper = styled.div<{ $isBottomNavigator?: boolean }>`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: auto;
  height: 100%;
  width: 100%;
  ${(p) =>
    p.$isBottomNavigator &&
    `
  padding: 0 ${ONBOARDING_PHONE_GUTTER}px;
  `};
`;
